import React, { useMemo, useCallback } from 'react';
import { TouchableOpacity, View, StyleSheet } from 'react-native';
import { Text } from './ui/AppText';
import FormattedText from './FormattedText';

const TrueFalseItem = ({
  item,
  index,
  selectedAnswer, // 'C' ou 'E'
  showResult,
  onPress,
  disabled,
}) => {
  // Memoiza cálculo de estilo para cada opção
  const getOptionStyle = useCallback((option) => {
    const isSelected = selectedAnswer === option;

    if (!showResult) {
      return {
        backgroundColor: isSelected ? '#3B82F6' : '#F3F4F6',
        borderColor: isSelected ? '#3B82F6' : '#E5E7EB',
        textColor: isSelected ? '#FFFFFF' : '#1F2937',
      };
    }

    const isCorrectOption = (item.isCorrect && option === 'C') || (!item.isCorrect && option === 'E');

    if (isCorrectOption) {
      return {
        backgroundColor: '#10B98130',
        borderColor: '#10B981',
        textColor: '#059669',
      };
    }

    if (isSelected && !isCorrectOption) {
      return {
        backgroundColor: '#EF444430',
        borderColor: '#EF4444',
        textColor: '#DC2626',
      };
    }

    return {
      backgroundColor: '#F3F4F6',
      borderColor: '#E5E7EB',
      textColor: '#6B7280',
    };
  }, [showResult, selectedAnswer, item.isCorrect]);

  // Memoiza estilos das opções
  const correctStyle = useMemo(() => getOptionStyle('C'), [getOptionStyle]);
  const wrongStyle = useMemo(() => getOptionStyle('E'), [getOptionStyle]);

  // Memoiza handlers
  const handleCorrectPress = useCallback(() => {
    if (onPress) onPress(item.id, 'C');
  }, [onPress, item.id]);

  const handleWrongPress = useCallback(() => {
    if (onPress) onPress(item.id, 'E');
  }, [onPress, item.id]);

  // Memoiza cor do texto do item
  const itemTextColor = useMemo(() => {
    if (!showResult) return '#1F2937';
    
    const expectedAnswer = item.isCorrect ? 'C' : 'E';
    if (selectedAnswer === expectedAnswer) return '#059669';
    if (selectedAnswer) return '#DC2626';
    return '#1F2937';
  }, [showResult, selectedAnswer, item.isCorrect]);

  // Memoiza indicador de resultado
  const resultIndicator = useMemo(() => {
    if (!showResult) return null;
    
    const expectedAnswer = item.isCorrect ? 'C' : 'E';
    if (selectedAnswer === expectedAnswer) {
      return <Text style={styles.correctIcon}>✓</Text>;
    }
    if (selectedAnswer) {
      return <Text style={styles.wrongIcon}>✗</Text>;
    }
    return <Text style={styles.notAnsweredIcon}>—</Text>;
  }, [showResult, selectedAnswer, item.isCorrect]);

  // Memoiza estilos dos botões
  const correctButtonStyle = useMemo(() => [
    styles.optionButton,
    { backgroundColor: correctStyle.backgroundColor, borderColor: correctStyle.borderColor },
  ], [correctStyle.backgroundColor, correctStyle.borderColor]);

  const wrongButtonStyle = useMemo(() => [
    styles.optionButton,
    { backgroundColor: wrongStyle.backgroundColor, borderColor: wrongStyle.borderColor },
  ], [wrongStyle.backgroundColor, wrongStyle.borderColor]);

  return (
    <View style={styles.container}>
      <View style={styles.optionsContainer}>
        <TouchableOpacity
          style={correctButtonStyle}
          onPress={handleCorrectPress}
          disabled={disabled}
          activeOpacity={0.7}
        >
          <Text style={[styles.optionText, { color: correctStyle.textColor }]}>
            C
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={wrongButtonStyle}
          onPress={handleWrongPress}
          disabled={disabled}
          activeOpacity={0.7}
        >
          <Text style={[styles.optionText, { color: wrongStyle.textColor }]}>
            E
          </Text>
        </TouchableOpacity>
      </View>

      <FormattedText style={[styles.itemText, { color: itemTextColor }]}>
        {item.text}
      </FormattedText>

      {resultIndicator && (
        <View style={styles.resultIndicator}>
          {resultIndicator}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    marginVertical: 6,
  },
  optionsContainer: {
    flexDirection: 'column',
    marginRight: 12,
    gap: 4,
  },
  optionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
  },
  optionText: {
    fontSize: 13,
    fontWeight: 'bold',
  },
  itemText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
  },
  resultIndicator: {
    marginLeft: 8,
  },
  correctIcon: {
    color: '#10B981',
    fontSize: 20,
    fontWeight: 'bold',
  },
  wrongIcon: {
    color: '#EF4444',
    fontSize: 20,
    fontWeight: 'bold',
  },
  notAnsweredIcon: {
    color: '#9CA3AF',
    fontSize: 20,
    fontWeight: 'bold',
  },
});

// Memoiza o componente - só re-renderiza se props mudarem
export default React.memo(TrueFalseItem);
